import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';

import { set as _set, cloneDeep, cloneDeepWith } from 'lodash';
import { ZodError, ZodIssue, ZodObject } from 'zod';

export type DeepKeyOf<T> = T extends object
  ? {
      [K in keyof T]-?: K | `${K & string}.${DeepKeyOf<T[K]> & string}` | `${K & string}[${number}]`;
    }[keyof T]
  : never;

export type DeepValue<T, K extends string> = K extends `${infer First}.${infer Rest}`
  ? First extends keyof T
    ? DeepValue<T[First], Rest>
    : never
  : K extends `${infer Index}`
  ? Index extends keyof T
    ? T[Index]
    : never
  : K extends keyof T
  ? T[K]
  : never;

type ZodSchema = ZodObject<Record<string, any>>;

interface FormProps<T extends Record<string, any>, K extends Record<string, any> | null> {
  initialState: T;
  errors: K;
  validationSchema?: ZodSchema;
}

function getFormattedZodErrors(errors: ZodIssue[]) {
  const formattedErrors: Record<string, Array<string>> = {};

  for (const error of errors) {
    const path = error.path.join('.');

    if (path in formattedErrors) {
      formattedErrors[path].push(error.message);
    } else {
      formattedErrors[path] = [error.message];
    }
  }

  return formattedErrors;
}

const useForm = <T extends Record<string, any>, K extends Record<string, any> | null>({
  initialState,
  validationSchema,
  errors,
}: FormProps<T, K>) => {
  const [data, setData] = useState<T>(initialState);
  const [formErrors, setFormErrors] = useState<K>(errors);

  const validateField = useCallback(
    (fieldName: string, clonedData: T) => {
      let clonedErrors = cloneDeep(formErrors);

      if (clonedErrors) clonedErrors = _set(clonedErrors, fieldName, '');

      try {
        validationSchema?.parse(clonedData);
        return clonedErrors;
      } catch (error) {
        if (error instanceof ZodError) {
          if (clonedErrors) {
            const formattedErrors = getFormattedZodErrors(error.errors);
            const currentFieldError = formattedErrors[fieldName]?.[0] || '';

            clonedErrors = _set(clonedErrors, fieldName, currentFieldError);
            // clonedErrors[fieldName] = error.errors[0].message;
          }
          return clonedErrors;
        }
      }
      return clonedErrors;
    },
    [validationSchema, formErrors]
  );

  const isValidForm = useMemo(() => {
    if (validationSchema) {
      try {
        validationSchema.parse(data);
        return true;
      } catch (error) {
        if (error instanceof ZodError) {
          // console.log(error.errors);
        }
        return false;
      }
    }
    return true;
  }, [data, validationSchema]);

  const setFieldValue = async <K extends string & DeepKeyOf<T>>(fieldName: K, fieldValue: DeepValue<T, K>) => {
    setData(prevData => {
      const clonedData = cloneDeep(prevData);

      const updatedData = _set(clonedData, fieldName, fieldValue);

      if (validationSchema) {
        const _errors = validateField(fieldName, updatedData);
        setFormErrors(_errors);
      }

      return updatedData;
    });
  };

  const handleChange = async ({
    currentTarget: input,
  }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const fieldName = input.name;
    const fieldValue = input.value as T[keyof T];

    setData(prevData => {
      const clonedData = cloneDeep(prevData);

      const updatedData = _set(clonedData, fieldName, fieldValue);

      if (validationSchema) {
        const _errors = validateField(fieldName, updatedData);
        setFormErrors(_errors);
      }

      return updatedData;
    });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>, handler: () => void | Promise<void>) => {
    event.preventDefault();

    if (validationSchema) {
      try {
        validationSchema.parse(data);
        handler();
      } catch (error) {
        if (error instanceof ZodError) {
          let updatedErrors = cloneDeep(formErrors) as K extends Record<string, any> ? K : never;
          const formattedErrors = getFormattedZodErrors(error.errors);

          for (let path in formattedErrors) {
            updatedErrors = _set(updatedErrors, path, formattedErrors[path]?.[0] ?? '');
          }

          setFormErrors(updatedErrors);
        }
      }
    }
  };

  const resetData = (overrideInitialState?: Record<string, any>) => {
    setData({ ...initialState, ...(overrideInitialState || {}) });

    if (errors) {
      setFormErrors(errors);
    }
  };

  const trimStringValues = (input: any): any => {
    if (typeof input === 'string') {
      return input.trim();
    } else if (Array.isArray(input)) {
      return input.map(trimStringValues);
    } else if (typeof input === 'object' && input !== null) {
      const trimmedObject: Record<string, any> = {};
      for (const key in input) {
        trimmedObject[key] = trimStringValues(input[key]);
      }
      return trimmedObject;
    }
    return input;
  };

  const trimFormData = (obj: T): T => {
    return cloneDeepWith(obj, trimStringValues) as T;
  };

  return {
    data,
    errors: formErrors,
    setFormErrors,
    isValidForm,
    handleChange,
    setFieldValue,
    onSubmit,
    resetData,
    trimFormData,
  };
};

export default useForm;
